import { Box, Flex, WizardStep } from '@auviknetworks/iceberg-ui';
import { useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useMemo, useState } from 'react';

const useWizardStep = (initialCount, numberOfSteps) => {
    const [currentStep, setCurrentStep] = useState(initialCount);

    const onStepChange = useCallback(
        (selectedStepNumber) => {
            if (selectedStepNumber >= numberOfSteps || selectedStepNumber < 0) {
                return;
            }
            setCurrentStep(selectedStepNumber);
        },
        [numberOfSteps]
    );
    const toStep = useCallback((step) => onStepChange(step), [onStepChange]);
    const toNextStep = useCallback(
        () => onStepChange(currentStep + 1),
        [currentStep, onStepChange]
    );
    const toPreviousStep = useCallback(
        () => onStepChange(currentStep - 1),
        [currentStep, onStepChange]
    );

    return {
        currentStep,
        toNextStep,
        toPreviousStep,
        toStep
    };
};

const WizardStepStatusType = {
    COMPLETED: 'completed',
    CURRENT: 'current',
    PENDING: 'pending'
};

const WizardAlign = {
    Horizontal: 'column',
    Vertical: 'row'
};

export const WizardSection = ({ isVisible, children }) =>
    isVisible && (
        <motion.div
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            style={{ height: '100%', width: '100%' }}
        >
            {children}
        </motion.div>
    );

export const calculateWizardStatus = (currentStep, stepNumber) => {
    if (currentStep === stepNumber) {
        return WizardStepStatusType.CURRENT;
    }
    if (currentStep > stepNumber) {
        return WizardStepStatusType.COMPLETED;
    }
    return WizardStepStatusType.PENDING;
};

export const invertFlexAlign = (align) =>
    align === WizardAlign.Horizontal
        ? WizardAlign.Vertical
        : WizardAlign.Horizontal;

export const WizardContext = React.createContext({});

export const Wizard = ({
    align = 'Horizontal',
    allowStepsNav = false,
    initialStep = 0,
    stepLabel,
    cancelButton,
    showLabel = true,
    wizardBodyProps,
    wizardFooterProps,
    children
}) => {
    const theme = useTheme();
    const Alignment = WizardAlign[align];
    const InvertedAlign = invertFlexAlign(Alignment);
    const filteredChildren = children.filter((c) => !!c);
    const { currentStep, toNextStep, toPreviousStep, toStep } = useWizardStep(
        initialStep,
        filteredChildren.length
    );

    const value = useMemo(
        () => ({
            toNextStep,
            toPreviousStep,
            toStep
        }),
        [toNextStep, toPreviousStep, toStep]
    );

    return (
        <Box data-testid="wizardContainer">
            <WizardContext.Provider value={value}>
                <Flex
                    data-testid="wizard"
                    flexDirection={InvertedAlign}
                    {...wizardBodyProps}
                >
                    <Flex
                        flexDirection={Alignment}
                        sx={{ height: '100%', width: '100%' }}
                    >
                        <Flex
                            data-testid="wizardSteps"
                            flexDirection={InvertedAlign}
                            flexGrow={0}
                            minWidth="250px"
                            padding="24px"
                            paddingBottom="48px"
                            width="250px"
                        >
                            {filteredChildren.map(
                                ({ props: { labelText, step } }, index) => (
                                    <WizardStep
                                        key={`step-${step}`}
                                        stepNumber={index}
                                        stepLabel={stepLabel}
                                        status={calculateWizardStatus(
                                            currentStep,
                                            index
                                        )}
                                        onClick={
                                            allowStepsNav
                                                ? () => toStep(index)
                                                : undefined
                                        }
                                        showLabel={showLabel}
                                        showSeparation={
                                            index < filteredChildren.length - 1
                                        }
                                        align={InvertedAlign}
                                    >
                                        {labelText}
                                    </WizardStep>
                                )
                            )}
                        </Flex>
                        <Flex
                            data-testid="wizardSections"
                            flexDirection={Alignment}
                            flexGrow={1}
                            key="wizardSections"
                        >
                            <Box
                                borderLeft="1px solid"
                                borderColor={theme.palette.border.primary}
                                width="100%"
                                height="100%"
                                ml={4}
                            >
                                <AnimatePresence>
                                    {filteredChildren.map(
                                        (
                                            {
                                                props: {
                                                    children: content,
                                                    step
                                                }
                                            },
                                            index
                                        ) => (
                                            <WizardSection
                                                key={`section-${step}`}
                                                isVisible={
                                                    currentStep === index
                                                }
                                            >
                                                {content}
                                            </WizardSection>
                                        )
                                    )}
                                </AnimatePresence>
                            </Box>
                        </Flex>
                    </Flex>
                </Flex>
                <Flex
                    data-testid="wizardFooter"
                    sx={{
                        backgroundColor: theme.palette.background.primaryLight,
                        justifyContent: 'space-between',
                        padding: '11px',
                        ...wizardFooterProps
                    }}
                >
                    <Flex>{cancelButton}</Flex>
                    <Flex style={{ gap: '10px' }}>
                        {filteredChildren.map(({ props: { buttons } }, index) =>
                            index === currentStep && buttons
                                ? buttons({ toNextStep, toPreviousStep })
                                : null
                        )}
                    </Flex>
                </Flex>
            </WizardContext.Provider>
        </Box>
    );
};
